import React, { useState, useEffect } from "react";
import Dialog from "@components/Dialog";
import Text from "@components/Text";
import { Row, Column } from "@components/Flex";
import Button from "@components/Button";
import Input from "@components/Input";
import { toast } from "react-toastify";
import { useLang } from "@src/hooks/useLang";
import { observer } from "mobx-react-lite";
import { uploadPrescriptionToFiles } from "@services/fieldsService";
import { getJohnDeereStatus, safeDeereUrl } from "@services/johnDeereService";
import type { PrescriptionLayersOption } from "@services/mowerService";
import Loading from "@components/Loading";
import { buildZoneRates, type Zone } from "@screens/PrescriptionScreen/rates";

interface IProps {
  fieldId: string;
  fieldName: string | null;
  seasonId: string | undefined;
  combined?: PrescriptionLayersOption;
  zones: Zone[];
  zoneCount: number;
  standard: number;
  visible: boolean;
  onClose: () => void;
}

const UploadToFilesDialog: React.FC<IProps> = ({
  fieldId,
  fieldName,
  seasonId,
  combined,
  zones,
  zoneCount,
  standard,
  visible,
  onClose,
}) => {
  const lang = useLang();
  const jdLang = lang.johnDeere;

  const [statusLoading, setStatusLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [termsUrl, setTermsUrl] = useState<string | null>(null);

  const rates = buildZoneRates(zones, standard);

  useEffect(() => {
    if (!visible) return;
    setStatusLoading(true);
    getJohnDeereStatus()
      .then((s) => setConnected(s.connected))
      .catch(() => setConnected(false))
      .finally(() => setStatusLoading(false));
  }, [visible]);

  const defaultFileName = (fieldName ?? fieldId).replace(/\s+/g, "_");

  useEffect(() => {
    if (visible) {
      setFileName(defaultFileName);
    }
  }, [visible, defaultFileName]);

  const isCombined = !!combined && combined.layers.length > 0;

  const handleUpload = async () => {
    if (!isCombined && !seasonId) {
      toast.error("Season not selected");
      return;
    }

    const name = fileName.trim() || defaultFileName;
    if (!name) {
      toast.error("File name is required");
      return;
    }

    setUploadLoading(true);
    setTermsUrl(null);
    try {
      await uploadPrescriptionToFiles(fieldId, {
        seasonId,
        layers: isCombined ? combined!.layers : undefined,
        surface: isCombined ? combined!.surface : undefined,
        zones: zoneCount,
        rates,
        fileName: name,
      });
      toast.success(
        (jdLang as any).uploadToFilesSuccess ?? "Prescription saved to files"
      );
      onClose();
    } catch (e: any) {
      // Only https links on John Deere's domain reach an href (safeDeereUrl).
      const termsLocation = safeDeereUrl((e as { termsLocation?: string }).termsLocation);
      const connectionsUrl = safeDeereUrl((e as { connectionsUrl?: string }).connectionsUrl);
      if (termsLocation) {
        setTermsUrl(termsLocation);
        toast.info(
          (jdLang as any).termsRequired ??
            "You may need to accept terms. Please visit the link below."
        );
      } else if (connectionsUrl) {
        toast.error(
          <span>
            Grant <strong>Organization</strong>, <strong>Locations</strong>, and <strong>Work</strong> (including Files) at{" "}
            <a href={connectionsUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline" }}>
              connections.deere.com
            </a>{" "}
            to upload prescriptions to files.
          </span>,
          { autoClose: false }
        );
      } else {
        toast.error(e?.message ?? "Upload failed");
      }
    } finally {
      setUploadLoading(false);
    }
  };

  const notConnectedMessage =
    (jdLang as any).connectToUpload ??
    "Connect John Deere in your profile to upload prescriptions to files.";

  return (
    <Dialog
      title={
        (jdLang as any).uploadToFilesTitle ?? "Upload prescription to files"
      }
      visible={visible}
      onClose={onClose}
      style={{ maxWidth: 500 }}
    >
      {statusLoading ? (
        <Loading />
      ) : !connected ? (
        <Column crossAxisSize="max">
          <Text size={14} color="#6B7280">
            {notConnectedMessage}
          </Text>
          <Row justifyContent="flex-end" style={{ marginTop: 16 }}>
            <Button onClick={onClose} kind="outline">
              {jdLang.close}
            </Button>
          </Row>
        </Column>
      ) : (
      <Column crossAxisSize="max" style={{ gap: 16 }}>
        <Text size={14} color="#6B7280">
          {(jdLang as any).uploadToFilesDescription ??
            `Save prescription map for field "${fieldName ?? fieldId}" with ${zoneCount} zones as a John Deere shapefile to your files.`}
        </Text>

        {termsUrl && (
          <Column crossAxisSize="max">
            <Text size={14} color="#6B7280">
              {(jdLang as any).acceptTerms ?? "You may need to accept terms:"}
            </Text>
            <a
              href={termsUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#68ad18", marginTop: 4 }}
            >
              {termsUrl}
            </a>
          </Column>
        )}

        <div>
          <Text size={14} style={{ marginBottom: 4, fontWeight: 600 }}>
            {(jdLang as any).fileNameLabel ?? "File name"}
          </Text>
          <Text size={12} color="#6B7280" style={{ marginBottom: 8 }}>
            {(jdLang as any).fileNameHint ??
              "Name for the shapefile (without extension). Uses field name by default."}
          </Text>
          <Input
            type="text"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            placeholder={defaultFileName}
          />
        </div>

        <Row justifyContent="flex-end" style={{ gap: 8, marginTop: 8 }}>
          <Button onClick={onClose} kind="outline">
            {jdLang.cancel}
          </Button>
          <Button onClick={handleUpload} disabled={uploadLoading}>
            {uploadLoading ? (
              <Loading />
            ) : (
              (jdLang as any).uploadToFilesButton ?? "Upload to Deere"
            )}
          </Button>
        </Row>
      </Column>
      )}
    </Dialog>
  );
};

export default observer(UploadToFilesDialog);
